import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Building2, Check, Crown, LogOut, Pencil, Search, UserMinus, UserPlus, Users, X } from 'lucide-react';
import {
  Alert, Button, Checkbox, CountBadge, Drawer, EmptyState, EntityAvatar, Input, Modal, SkeletonText, Tag, cn, message,
} from '../../ui';
import { RoleBadge } from '../../components/RoleBadge';
import { useConfirmedMutation } from '../../lib/confirm';
import { IST_TZ, formatIst } from '../../lib/time';
import { useDebounce } from '../../hooks/useDebounce';
import { useAuthStore } from '../../store/auth';
import { plural } from '../../lib/format';
import {
  chatApi, chatErr, chatKeys, conversationPreview, conversationTitle,
  type ChatConversation, type ChatConversationDetail, type ChatPerson,
} from './chatApi';

/* ───────────────────────── Formatting ───────────────────────── */

const ist = (iso: string) => dayjs(iso).tz(IST_TZ);

/** List-row time: "09:12 AM" today, "Yesterday", weekday within the week, else "06 Jun". */
export function listTime(iso?: string | null): string {
  if (!iso) return '';
  const d = ist(iso);
  if (!d.isValid()) return '';
  const today = dayjs().tz(IST_TZ).startOf('day');
  const day = d.startOf('day');
  const diff = today.diff(day, 'day');
  if (diff <= 0) return formatIst(iso, 'hh:mm A');
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return formatIst(iso, 'ddd');
  if (d.year() === today.year()) return formatIst(iso, 'DD MMM');
  return formatIst(iso, 'DD MMM YYYY');
}

/** IST calendar-day key used to group a thread. */
export const dayKey = (iso: string) => ist(iso).format('YYYY-MM-DD');

/** The divider between days in a thread. */
export function dayLabel(iso: string): string {
  const today = dayjs().tz(IST_TZ).startOf('day');
  const diff = today.diff(ist(iso).startOf('day'), 'day');
  if (diff <= 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return formatIst(iso, 'ddd, DD MMM YYYY');
}

export const isImageUrl = (u: string) => /\.(png|jpe?g|webp|gif|svg|heic|heif|bmp)(\?|$)/i.test(u);

/* ───────────────────────── Identity marks ───────────────────────── */

/** The room's mark: the other person's photo/initials for DIRECT, a building for COMPANY, initials otherwise. */
export function ConversationAvatar({ c, size = 40 }: { c: ChatConversation; size?: number }) {
  const title = conversationTitle(c);
  if (c.type === 'DIRECT') return <EntityAvatar name={c.otherUser?.name ?? title} src={c.otherUser?.photoUrl} size={size} />;
  if (c.type === 'COMPANY') return <EntityAvatar name={title} icon={<Building2 className="size-[45%]" />} size={size} square />;
  if (c.type === 'TEAM') return <EntityAvatar name={title} icon={<Users className="size-[45%]" />} size={size} square />;
  return <EntityAvatar name={title} size={size} square />;
}

/** COMPANY and TEAM rooms are system rooms — tagged so they read differently from a hand-made group. */
export function ConversationTypeTag({ type, className }: { type: ChatConversation['type']; className?: string }) {
  if (type === 'COMPANY') return <Tag tone="green" className={className}>Company</Tag>;
  if (type === 'TEAM') return <Tag tone="gold" className={className}>Team</Tag>;
  return null;
}

/* ───────────────────────── Conversation list row ───────────────────────── */

/**
 * One row of the conversation list — avatar, title, Company/Team tag, time, preview, unread pill.
 * Used by the full-screen page and (with `compact`) by the floating chat panel.
 */
export function ConversationRow({
  c, active = false, compact = false, onOpen,
}: { c: ChatConversation; active?: boolean; compact?: boolean; onOpen: (id: string) => void }) {
  const me = useAuthStore((s) => s.user);
  const unread = c.unread > 0 && !active;
  return (
    <button
      type="button"
      onClick={() => onOpen(c.id)}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex w-full items-center border-b border-line/60 text-left transition-colors',
        compact ? 'gap-2.5 px-3 py-2' : 'gap-3 px-4 py-2.5',
        active ? 'bg-brand-50 dark:bg-brand-900/25' : 'hover:bg-surface-2',
      )}
    >
      <ConversationAvatar c={c} size={compact ? 36 : 42} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className={cn('truncate text-content', unread ? 'type-body-strong' : 'type-body-medium')}>
            {conversationTitle(c)}
          </span>
          <ConversationTypeTag type={c.type} className="shrink-0" />
          <span className={cn('ml-auto shrink-0 text-2xs', unread ? 'text-brand-700 dark:text-brand-300' : 'text-subtle')}>
            {listTime(c.lastMessage?.createdAt)}
          </span>
        </span>
        <span className="mt-0.5 flex items-center gap-2">
          <span className={cn('min-w-0 flex-1 truncate type-caption', unread ? 'text-content' : 'text-subtle')}>
            {conversationPreview(c, me?.id)}
          </span>
          {unread && <CountBadge count={c.unread} className="ring-0" />}
        </span>
      </span>
    </button>
  );
}

/* ───────────────────────── People picker ───────────────────────── */

/**
 * Search-as-you-type over `GET /chat/people?q=` (active staff, excluding me, max 50).
 * `single` calls `onPick` on a click; `multi` toggles membership of `selected`.
 */
function PeoplePicker({
  mode, selected = [], onToggle, onPick, excludeIds = [], busyId,
}: {
  mode: 'single' | 'multi';
  selected?: ChatPerson[];
  onToggle?: (p: ChatPerson) => void;
  onPick?: (p: ChatPerson) => void;
  excludeIds?: string[];
  /** Row showing a spinner (single mode, while its chat opens). */
  busyId?: string | null;
}) {
  const [q, setQ] = useState('');
  const dq = useDebounce(q.trim(), 300);
  const peopleQ = useQuery({
    queryKey: chatKeys.people(dq),
    queryFn: () => chatApi.people(dq),
    staleTime: 30_000,
  });
  const exclude = useMemo(() => new Set(excludeIds), [excludeIds]);
  const selectedIds = useMemo(() => new Set(selected.map((p) => p.id)), [selected]);
  const rows = (peopleQ.data ?? []).filter((p) => !exclude.has(p.id));

  return (
    <div className="flex flex-col gap-3">
      <Input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by name or mobile"
        prefix={<Search />}
        aria-label="Search people"
      />
      {mode === 'multi' && selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((p) => (
            <Tag key={p.id} tone="green" closable onClose={() => onToggle?.(p)}>{p.name}</Tag>
          ))}
        </div>
      )}
      <div className="max-h-[46vh] min-h-[180px] overflow-y-auto rounded-control border border-line">
        {peopleQ.isLoading ? (
          <div className="p-3"><SkeletonText lines={5} /></div>
        ) : peopleQ.isError ? (
          <div className="p-3"><Alert type="error" message={chatErr(peopleQ.error, 'Could not load people')} /></div>
        ) : rows.length === 0 ? (
          <EmptyState compact icon={<Users />} title={dq ? `Nobody matches “${dq}”` : 'Nobody to add'} />
        ) : (
          rows.map((p) => {
            const on = selectedIds.has(p.id);
            return (
              <button
                key={p.id}
                type="button"
                disabled={!!busyId}
                onClick={() => (mode === 'single' ? onPick?.(p) : onToggle?.(p))}
                className={cn(
                  'flex w-full items-center gap-3 border-b border-line/60 px-3 py-2 text-left transition-colors last:border-b-0 hover:bg-surface-2 disabled:opacity-60',
                  on && 'bg-brand-50/60 dark:bg-brand-900/20',
                )}
              >
                {mode === 'multi' && (
                  <span onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={on} onCheckedChange={() => onToggle?.(p)} aria-label={`Select ${p.name}`} />
                  </span>
                )}
                <EntityAvatar name={p.name} src={p.photoUrl} size={32} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate type-body-strong text-content">{p.name}</span>
                </span>
                <RoleBadge role={p.roleLabel || p.role} />
                {busyId === p.id && <span className="type-caption text-subtle">Opening…</span>}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

/* ───────────────────────── New direct chat ───────────────────────── */

/**
 * `onOpened` receives the room once it exists; without it the dialog navigates to `/chat/<id>`
 * (the full-screen page). The floating chat panel passes it to open the room in place.
 */
export function NewChatDialog({ open, onClose, onOpened }: { open: boolean; onClose: () => void; onOpened?: (id: string) => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  // Find-or-create: opening a 1-to-1 chat with someone you already talk to returns the same room,
  // so this is navigation, not a data change the user has to confirm.
  const open1to1 = useMutation({
    mutationFn: (p: ChatPerson) => chatApi.direct(p.id),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: chatKeys.list });
      onClose();
      if (onOpened) onOpened(c.id); else navigate(`/chat/${c.id}`);
    },
    onError: (e) => message.error(chatErr(e, 'Could not open the chat')),
  });
  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title="New chat" description="Pick a colleague to message one-to-one." width={480}>
      {open && (
        <PeoplePicker
          mode="single"
          onPick={(p) => open1to1.mutate(p)}
          busyId={open1to1.isPending ? open1to1.variables?.id : null}
        />
      )}
    </Modal>
  );
}

/* ───────────────────────── New group ───────────────────────── */

/** `onOpened` — see NewChatDialog. */
export function NewGroupDialog({ open, onClose, onOpened }: { open: boolean; onClose: () => void; onOpened?: (id: string) => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [members, setMembers] = useState<ChatPerson[]>([]);
  useEffect(() => { if (!open) { setTitle(''); setMembers([]); } }, [open]);

  const toggle = (p: ChatPerson) =>
    setMembers((arr) => (arr.some((m) => m.id === p.id) ? arr.filter((m) => m.id !== p.id) : [...arr, p]));

  const create = useConfirmedMutation(
    () => ({
      title: `Create the group “${title.trim()}”?`,
      content: `It starts with you and ${plural(members.length, 'other person', 'other people')}. You can rename it and add or remove people later.`,
      okText: 'Yes, create group',
    }),
    {
      mutationFn: () => chatApi.createGroup(title.trim(), members.map((m) => m.id)),
      onSuccess: (c) => {
        qc.invalidateQueries({ queryKey: chatKeys.list });
        onClose();
        if (onOpened) onOpened(c.id); else navigate(`/chat/${c.id}`);
      },
      onError: (e) => message.error(chatErr(e, 'Could not create the group')),
    },
  );

  const canCreate = title.trim().length > 0 && members.length > 0;
  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title="New group"
      width={520}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!canCreate} loading={create.isPending} onClick={() => create.mutate()}>
            Create group
          </Button>
        </>
      }
    >
      {open && (
        <div className="flex flex-col gap-4">
          <div>
            <label className="mb-1.5 block type-label-strong text-content" htmlFor="chat-group-title">Group name</label>
            <Input
              id="chat-group-title"
              value={title}
              maxLength={120}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Kharif launch — Nashik"
            />
          </div>
          <div>
            <div className="mb-1.5 type-label-strong text-content">
              Members {members.length > 0 && <span className="text-subtle">· {members.length} selected</span>}
            </div>
            <PeoplePicker mode="multi" selected={members} onToggle={toggle} />
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ───────────────────────── Info drawer ───────────────────────── */

/** `onLeft` runs after leaving a group; without it the drawer navigates to `/chat`. */
export function ChatInfoDrawer({
  open, onClose, detail, onLeft,
}: { open: boolean; onClose: () => void; detail: ChatConversationDetail | undefined; onLeft?: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const [toAdd, setToAdd] = useState<ChatPerson[]>([]);
  useEffect(() => { if (!open) { setEditing(false); setAdding(false); setToAdd([]); } }, [open]);

  const id = detail?.id ?? '';
  const isGroup = detail?.type === 'GROUP';
  const manage = !!detail?.canManage && isGroup;
  const name = detail ? conversationTitle(detail) : '';

  const applyDetail = (d: ChatConversationDetail) => {
    qc.setQueryData(chatKeys.detail(id), d);
    qc.invalidateQueries({ queryKey: chatKeys.list });
  };

  const rename = useConfirmedMutation(
    () => ({ title: `Rename this group to “${title.trim()}”?`, content: 'Everyone in the group sees the new name.', okText: 'Yes, rename' }),
    {
      mutationFn: () => chatApi.rename(id, title.trim()),
      onSuccess: (d) => { applyDetail(d); setEditing(false); message.success('Group renamed'); },
      onError: (e) => message.error(chatErr(e, 'Could not rename the group')),
    },
  );

  const addMembers = useConfirmedMutation(
    () => ({
      title: `Add ${plural(toAdd.length, 'person', 'people')} to ${name}?`,
      content: 'They can read and post in the group from now on.',
      okText: 'Yes, add',
    }),
    {
      mutationFn: () => chatApi.addMembers(id, toAdd.map((p) => p.id)),
      onSuccess: (d) => { applyDetail(d); setAdding(false); setToAdd([]); message.success('Members added'); },
      onError: (e) => message.error(chatErr(e, 'Could not add members')),
    },
  );

  const removeMember = useConfirmedMutation(
    (p: ChatPerson) => ({
      title: `Remove ${p.name} from ${name}?`,
      content: 'They stop receiving messages from this group. You can add them back later.',
      okText: 'Yes, remove',
      danger: true,
    }),
    {
      mutationFn: (p: ChatPerson) => chatApi.removeMember(id, p.id),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: chatKeys.detail(id) });
        qc.invalidateQueries({ queryKey: chatKeys.list });
        message.success('Member removed');
      },
      onError: (e) => message.error(chatErr(e, 'Could not remove the member')),
    },
  );

  const leave = useConfirmedMutation(
    () => ({
      title: `Leave ${name}?`,
      content: 'You stop receiving its messages and it disappears from your chat list. Someone in the group has to add you back to rejoin.',
      okText: 'Yes, leave group',
      danger: true,
    }),
    {
      mutationFn: () => chatApi.removeMember(id, String(me?.id ?? '')),
      onSuccess: () => {
        qc.removeQueries({ queryKey: chatKeys.detail(id) });
        qc.invalidateQueries({ queryKey: chatKeys.list });
        qc.invalidateQueries({ queryKey: chatKeys.unread });
        onClose();
        message.success(`You left ${name}`);
        if (onLeft) onLeft(); else navigate('/chat');
      },
      onError: (e) => message.error(chatErr(e, 'Could not leave the group')),
    },
  );

  const members = detail?.members ?? [];
  const sorted = [...members].sort((a, b) => Number(b.isOwner) - Number(a.isOwner) || a.name.localeCompare(b.name));

  return (
    <Drawer open={open} onClose={onClose} title={detail?.type === 'DIRECT' ? 'Chat info' : 'Group info'} width={420}
      footer={isGroup ? (
        <Button block variant="soft-danger" icon={<LogOut size={16} />} loading={leave.isPending} onClick={() => leave.mutate()}>
          Leave group
        </Button>
      ) : undefined}
    >
      {!detail ? (
        <SkeletonText lines={6} />
      ) : (
        <div className="flex flex-col gap-5">
          {/* Identity + rename */}
          <div className="flex items-center gap-3">
            <ConversationAvatar c={detail} size={52} />
            <div className="min-w-0 flex-1">
              {editing ? (
                <div className="flex items-center gap-2">
                  <Input
                    autoFocus
                    value={title}
                    maxLength={120}
                    onChange={(e) => setTitle(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && title.trim()) rename.mutate(); if (e.key === 'Escape') setEditing(false); }}
                    aria-label="Group name"
                  />
                  <Button size="sm" variant="primary" icon={<Check size={16} />} aria-label="Save name"
                    disabled={!title.trim() || title.trim() === name} loading={rename.isPending} onClick={() => rename.mutate()} />
                  <Button size="sm" icon={<X size={16} />} aria-label="Cancel rename" onClick={() => setEditing(false)} />
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="truncate type-section-title text-content">{name}</span>
                  {manage && (
                    <Button size="sm" variant="text" icon={<Pencil size={16} />} aria-label="Rename group"
                      onClick={() => { setTitle(name); setEditing(true); }} />
                  )}
                </div>
              )}
              <div className="mt-0.5 flex items-center gap-2 type-caption text-subtle">
                <ConversationTypeTag type={detail.type} />
                <span>{plural(members.length || detail.memberCount, 'member')}</span>
              </div>
            </div>
          </div>

          {(detail.type === 'COMPANY' || detail.type === 'TEAM') && (
            <Alert
              type="info"
              message={detail.type === 'COMPANY'
                ? 'Every staff member is in this room automatically.'
                : 'This room holds a team lead and the people who report to them. Membership follows the reporting line automatically.'}
            />
          )}

          {/* Add members */}
          {manage && (
            adding ? (
              <div className="flex flex-col gap-2 rounded-card border border-line p-3">
                <div className="type-label-strong text-content">Add people</div>
                <PeoplePicker
                  mode="multi"
                  selected={toAdd}
                  excludeIds={members.map((m) => m.id)}
                  onToggle={(p) => setToAdd((arr) => (arr.some((x) => x.id === p.id) ? arr.filter((x) => x.id !== p.id) : [...arr, p]))}
                />
                <div className="flex justify-end gap-2">
                  <Button onClick={() => { setAdding(false); setToAdd([]); }}>Cancel</Button>
                  <Button variant="primary" disabled={!toAdd.length} loading={addMembers.isPending} onClick={() => addMembers.mutate()}>
                    {toAdd.length ? `Add ${toAdd.length}` : 'Add'}
                  </Button>
                </div>
              </div>
            ) : (
              <Button icon={<UserPlus size={16} />} onClick={() => setAdding(true)}>Add people</Button>
            )
          )}

          {/* Members */}
          <div>
            <div className="mb-2 type-overline text-subtle">Members</div>
            <div className="flex flex-col">
              {sorted.map((m) => {
                const isMe = String(m.id) === String(me?.id);
                return (
                  <div key={m.id} className="flex items-center gap-3 border-b border-line/60 py-2 last:border-b-0">
                    <EntityAvatar name={m.name} src={m.photoUrl} size={34} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate type-body-strong text-content">{m.name}</span>
                        {isMe && <span className="type-caption text-subtle">(you)</span>}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                        <RoleBadge role={m.roleLabel || m.role} />
                        {m.isOwner && isGroup && <Tag tone="gold" icon={<Crown size={12} />}>Owner</Tag>}
                      </div>
                    </div>
                    {manage && !isMe && (
                      <Button
                        size="sm"
                        variant="text"
                        icon={<UserMinus size={16} />}
                        aria-label={`Remove ${m.name}`}
                        title={`Remove ${m.name}`}
                        loading={removeMember.isPending && removeMember.variables?.id === m.id}
                        onClick={() => removeMember.mutate(m)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
}
